import { Platform } from 'react-native';
import { getApiBaseUrl } from './constant';

const getPasskeyBaseUrl = async () => {
  const baseUrl = await getApiBaseUrl();
  return baseUrl;
};

/**
 * Generate challenge for passkey registration
 */
export const generatePasskeyChallenge = async () => {
  try {
    const baseUrl = await getPasskeyBaseUrl();
    const response = await fetch(`${baseUrl}/passkey/challenge`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      let message = 'Failed to generate passkey challenge';
      try {
        const error = await response.json();
        message = error.error || message;
      } catch {
        // ignore non-JSON error bodies
      }
      throw new Error(message);
    }

    return await response.json();
  } catch (error) {
    console.error('Error generating passkey challenge:', error);
    throw error;
  }
};

/**
 * Register a new passkey
 */
export const registerPasskey = async (credential: any, deviceName?: string) => {
  try {
    const baseUrl = await getPasskeyBaseUrl();
    const response = await fetch(`${baseUrl}/passkey/register`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        credential,
        deviceName: deviceName || `${Platform.OS} Device`,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to register passkey');
    }

    return await response.json();
  } catch (error) {
    console.error('Error registering passkey:', error);
    throw error;
  }
};

/**
 * Generate authentication challenge for passkey login.
 * Omit email for discoverable / conditional passkey autofill.
 */
export const generatePasskeyAuthChallenge = async (email?: string) => {
  try {
    const baseUrl = await getPasskeyBaseUrl();
    const response = await fetch(`${baseUrl}/passkey/auth-challenge`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(email ? { email } : {}),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate authentication challenge');
    }

    return await response.json();
  } catch (error) {
    console.error('Error generating passkey auth challenge:', error);
    throw error;
  }
};

/**
 * Authenticate with passkey
 */
export const authenticatePasskey = async (credential: any) => {
  try {
    const baseUrl = await getPasskeyBaseUrl();
    const response = await fetch(`${baseUrl}/passkey/authenticate`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ credential }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to authenticate with passkey');
    }

    return await response.json();
  } catch (error) {
    console.error('Error authenticating with passkey:', error);
    throw error;
  }
};

/**
 * List user's passkeys
 */
export const listPasskeys = async () => {
  try {
    const baseUrl = await getPasskeyBaseUrl();
    const response = await fetch(`${baseUrl}/passkeys`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to list passkeys');
    }

    return await response.json();
  } catch (error) {
    console.error('Error listing passkeys:', error);
    throw error;
  }
};

/**
 * Delete a passkey
 */
export const deletePasskey = async (credentialId: string) => {
  try {
    const baseUrl = await getPasskeyBaseUrl();
    const response = await fetch(`${baseUrl}/passkey`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ credentialId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete passkey');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting passkey:', error);
    throw error;
  }
};

/**
 * Check if WebAuthn is supported.
 * Credentials are created via navigator.credentials.create — there is no
 * PublicKeyCredential.create static method (that check always failed in Chrome).
 */
export const isWebAuthnSupported = (): boolean => {
  if (Platform.OS !== 'web') {
    // For native platforms, WebAuthn support is limited
    // You may need to use platform-specific solutions
    return false;
  }

  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  return !!(
    window.PublicKeyCredential &&
    navigator.credentials &&
    typeof navigator.credentials.create === 'function'
  );
};



